import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, Copy, KeyRound, AlertTriangle } from "lucide-react";

/**
 * Self-contained Security card for an admin (or any user) to enroll, disable,
 * and rotate backup codes for TOTP 2FA. Renders inline inside the Settings page.
 *
 * State machine:
 *   idle (loading status) → status known
 *     - disabled: show "Enable 2FA" → enter password → setup → show QR → enter code → enable → show backup codes
 *     - enabled:  show "Disable 2FA" → password + code → disable
 */
export default function TwoFactorSetup({ onComplete }) {
  const [status, setStatus] = useState(null);        // {enabled, backup_codes_remaining}
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("idle");        // idle | password | qr | backup | disable
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setupData, setSetupData] = useState(null);  // {qr_code_data_url, secret, provisioning_uri}
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const reset = () => {
    setPhase("idle");
    setPassword("");
    setCode("");
    setSetupData(null);
    setBackupCodes(null);
  };

  const startEnroll = () => {
    reset();
    setPhase("password");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup", { password });
      setSetupData(data);
      setCode("");
      setPhase("qr");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const submitEnable = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes);
      setPhase("backup");
      toast.success("Two-factor authentication enabled");
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password, code });
      toast.success("Two-factor authentication disabled");
      reset();
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const submitRegenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { password, code });
      setBackupCodes(data.backup_codes);
      setPhase("backup");
      toast.success("Backup codes regenerated");
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate codes");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = () => {
    if (!backupCodes) return;
    navigator.clipboard
      .writeText(backupCodes.join("\n"))
      .then(() => toast.success("Backup codes copied"))
      .catch(() => toast.error("Could not copy — please copy manually"));
  };

  if (loading) {
    return (
      <div className="card-surface p-5 flex items-center text-clinical-subtext" data-testid="twofa-card">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading security…
      </div>
    );
  }

  const enabled = !!status?.enabled;

  return (
    <div className="card-surface p-5 space-y-4" data-testid="twofa-card">
      <div className="flex items-start gap-3">
        <div
          className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
            enabled ? "bg-emerald-50 text-emerald-600" : "bg-clinical-bg text-clinical-subtext"
          }`}
        >
          {enabled ? <ShieldCheck className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-medium flex items-center gap-2">
            Two-factor authentication
            {enabled && (
              <span
                className="text-[11px] font-medium uppercase tracking-wide bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full"
                data-testid="twofa-status-badge"
              >
                Enabled
              </span>
            )}
          </h2>
          <p className="mt-1 text-sm text-clinical-subtext">
            Add a second step at sign-in using an authenticator app like Google Authenticator,
            1Password, or Authy. Even if your password leaks, no one can sign in without your phone.
          </p>
          {enabled && (
            <p className="mt-1 text-xs text-clinical-subtext" data-testid="twofa-backup-remaining">
              {status.backup_codes_remaining} backup code
              {status.backup_codes_remaining === 1 ? "" : "s"} remaining.
            </p>
          )}
        </div>
        {phase === "idle" && (
          <div className="flex flex-col gap-2">
            {!enabled ? (
              <button
                type="button"
                onClick={startEnroll}
                className="btn-primary"
                data-testid="twofa-enable-button"
              >
                Enable 2FA
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { reset(); setPhase("disable"); }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white text-clinical-text px-3 py-1.5 text-sm font-medium hover:bg-clinical-bg"
                  data-testid="twofa-disable-button"
                >
                  <ShieldOff className="h-3.5 w-3.5" /> Disable
                </button>
                <button
                  type="button"
                  onClick={() => { reset(); setPhase("regenerate"); }}
                  className="text-xs text-clinical-primary hover:underline"
                  data-testid="twofa-regenerate-button"
                >
                  Regenerate backup codes
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Phase: password re-confirm (enroll start) */}
      {phase === "password" && (
        <form onSubmit={submitPassword} className="space-y-3 pt-2 border-t border-clinical-border" data-testid="twofa-password-form">
          <p className="text-xs text-clinical-subtext pt-3">
            Confirm your password to begin enrollment.
          </p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-clinical"
            placeholder="Current password"
            data-testid="twofa-password-input"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={reset} className="text-sm text-clinical-subtext px-3 py-1.5">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="twofa-password-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </button>
          </div>
        </form>
      )}

      {/* Phase: show QR + enter first code */}
      {phase === "qr" && setupData && (
        <form onSubmit={submitEnable} className="space-y-4 pt-2 border-t border-clinical-border" data-testid="twofa-qr-form">
          <div className="pt-3">
            <p className="text-sm text-clinical-text font-medium">1. Scan this QR code</p>
            <p className="text-xs text-clinical-subtext">In your authenticator app (Google Authenticator, 1Password, Authy…)</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <img
              src={setupData.qr_code_data_url}
              alt="2FA QR code"
              className="h-44 w-44 rounded-lg border border-clinical-border bg-white"
              data-testid="twofa-qr-image"
            />
            <div className="text-xs text-clinical-subtext space-y-2">
              <p>Can't scan? Enter this secret manually:</p>
              <code
                className="block bg-clinical-bg border border-clinical-border rounded p-2 font-mono text-[11px] break-all"
                data-testid="twofa-secret"
              >
                {setupData.secret}
              </code>
            </div>
          </div>
          <div>
            <p className="text-sm text-clinical-text font-medium">2. Enter the 6-digit code from your app</p>
            <input
              type="text"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="input-clinical mt-1.5 font-mono text-center tracking-[0.4em] text-xl"
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              data-testid="twofa-verify-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={reset} className="text-sm text-clinical-subtext px-3 py-1.5">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="btn-primary"
              data-testid="twofa-verify-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & enable"}
            </button>
          </div>
        </form>
      )}

      {/* Phase: show backup codes (one-time view) */}
      {phase === "backup" && backupCodes && (
        <div className="pt-2 border-t border-clinical-border space-y-3" data-testid="twofa-backup-screen">
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900">
              <p className="font-medium">Save these backup codes now.</p>
              <p>Each is single-use. Use one to sign in if you lose your phone. You won't see them again.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c, i) => (
              <div
                key={`backup-${c}`}
                className="bg-clinical-bg border border-clinical-border rounded px-2 py-1.5 text-center"
                data-testid={`twofa-backup-code-${i}`}
              >
                {c}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={copyAll}
              className="inline-flex items-center gap-1.5 text-sm text-clinical-primary hover:underline"
              data-testid="twofa-backup-copy"
            >
              <Copy className="h-3.5 w-3.5" /> Copy all
            </button>
            <button
              type="button"
              onClick={() => {
                setBackupCodes(null);
                reset();
                if (typeof onComplete === "function") onComplete();
              }}
              className="btn-primary"
              data-testid="twofa-backup-done"
            >
              I&apos;ve saved them
            </button>
          </div>
        </div>
      )}

      {/* Phase: disable */}
      {phase === "disable" && (
        <form onSubmit={submitDisable} className="space-y-3 pt-2 border-t border-clinical-border" data-testid="twofa-disable-form">
          <p className="text-xs text-clinical-subtext pt-3">
            Disabling 2FA will weaken your account. Confirm your password and current code to continue.
          </p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-clinical"
            placeholder="Current password"
            data-testid="twofa-disable-password"
          />
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input-clinical font-mono"
            placeholder="6-digit code or backup code"
            inputMode="numeric"
            data-testid="twofa-disable-code"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={reset} className="text-sm text-clinical-subtext px-3 py-1.5">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="twofa-disable-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable 2FA"}
            </button>
          </div>
        </form>
      )}

      {/* Phase: regenerate backup codes */}
      {phase === "regenerate" && (
        <form onSubmit={submitRegenerate} className="space-y-3 pt-2 border-t border-clinical-border" data-testid="twofa-regen-form">
          <p className="text-xs text-clinical-subtext pt-3">
            This invalidates all existing backup codes. Confirm with your password and a current code.
          </p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-clinical"
            placeholder="Current password"
            data-testid="twofa-regen-password"
          />
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input-clinical font-mono"
            placeholder="6-digit code from your app"
            inputMode="numeric"
            maxLength={6}
            data-testid="twofa-regen-code"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={reset} className="text-sm text-clinical-subtext px-3 py-1.5">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="twofa-regen-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate new codes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
